import { useEffect, useRef, useState } from "react"
import { Button, CircularProgress, FormControlLabel, Radio } from "@mui/material"
import removeImage from "@/assets/img/reservations/icons/remove.svg"
import { trans } from "@/_providers/Translation"
import Api from "@/_helpers/Api"

const FIELDS = [
    { name: 'bank_iban', label: () => 'IBAN' },
    { name: 'bank_name', label: () => trans('labels.bank') },
    { name: 'bank_swift', label: () => 'SWIFT' },
    { name: 'bank_sort_code', label: () => 'Sort Code' },
    { name: 'bank_account_number', label: () => 'Account number' },
]

export default function BankAccount({
    bank,
    index,
    disabled,
    onUpdateField,
    onSetDefault,
    onRemove,
    validations,
}) {

    const ibanTimeout = useRef(null)
    const latestIban = useRef(null)
    const [bankNameLoading, setBankNameLoading] = useState(false)

    useEffect(() => () => clearTimeout(ibanTimeout.current), [])

    const lookupBankName = iban => {
        clearTimeout(ibanTimeout.current)

        const cleaned = iban.replace(/\s+/g, '').toUpperCase()
        latestIban.current = cleaned
        setBankNameLoading(false)

        // Shortest valid IBAN is 15 characters
        if (cleaned.length < 15) return

        ibanTimeout.current = setTimeout(() => {
            setBankNameLoading(true)

            Api.get(`bank-name-by-iban/${encodeURIComponent(cleaned)}`)
                .then(res => {
                    // Ignore responses for an IBAN that has since changed
                    if (latestIban.current !== cleaned) return

                    const bankName = res.data?.bank_name

                    if (bankName) {
                        onUpdateField(index, 'bank_name', bankName)
                    }
                })
                .catch(() => { })
                .finally(() => {
                    if (latestIban.current === cleaned) {
                        setBankNameLoading(false)
                    }
                })
        }, 500)
    }

    const handleInputChange = e => {
        const { name, value } = e.target
        // Extract field name from banks[index][field_name] format
        const fieldName = name.match(/\[([^\]]+)\]$/)?.[1] || name

        onUpdateField(index, fieldName, value)

        if (fieldName === 'bank_iban') {
            lookupBankName(value)
        }
    }

    return (
        <div className="device-container">
            <div className="head">
                {`${trans('labels.bankAccount')} ${index + 1}`}
                <FormControlLabel
                    control={
                        <Radio
                            size="small"
                            checked={bank?.is_default ? true : false}
                            onChange={() => onSetDefault(index)}
                            disabled={disabled}
                        />
                    }
                    label={trans('labels.default')}
                    style={{
                        marginLeft: 'auto',
                        marginRight: 0
                    }}
                />
                {onRemove && (
                    <Button
                        type="button"
                        onClick={e => {
                            e.stopPropagation()
                            onRemove(index)
                        }}
                    >
                        <img src={removeImage} alt="Remove" />
                    </Button>
                )}
            </div>
            <div className="body">
                {FIELDS.map(field => {
                    const isLoading = field.name === 'bank_name' && bankNameLoading

                    return (
                        <div className="row" key={field.name}>
                            <div className="col">
                                <label>
                                    {field.label()}
                                </label>

                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        name={`banks[${index}][${field.name}]`}
                                        value={bank?.[field.name] || ''}
                                        className={validations && validations[`banks.${index}.${field.name}`] ? 'validate' : ''}
                                        onChange={handleInputChange}
                                        disabled={disabled}
                                        readOnly={isLoading}
                                        placeholder={isLoading ? trans('labels.loading') : undefined}
                                        style={isLoading ? { paddingRight: 36 } : undefined}
                                    />

                                    {isLoading && (
                                        <CircularProgress
                                            size={16}
                                            style={{
                                                position: 'absolute',
                                                right: 12,
                                                top: '50%',
                                                marginTop: -8,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                })}

                <input
                    type="hidden"
                    name={`banks[${index}][is_default]`}
                    value={bank?.is_default ? 1 : 0}
                    disabled={disabled}
                />

                {bank?.id ? (
                    <input
                        type="hidden"
                        name={`banks[${index}][id]`}
                        value={bank.id}
                        disabled={disabled}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
